<template>
    <section class="card ranking-table-section">
        <div class="table-toolbar">
            <el-button
                type="primary"
                class="generate-text-btn"
                :disabled="!isGenerateActive || isAnyGenerating"
                @click="handleGenerateText"
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="mr-2"
                >
                    <path
                        d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
                    />
                </svg>
                {{
                    $trans("web_skill_ranking.admin.generate_ai_content") ||
                    "Generate AI Content"
                }}
            </el-button>
        </div>

        <EasyDataTable
            :border-cell="false"
            :buttons-pagination="false"
            :headers="headers"
            :items="WebSkillRankingStore.activeProfiles"
            :rows-per-page="10"
            hide-footer
            table-class-name="custom-ranking-table"
        >
            <template #item-position="{ current_position }">
                <div class="position-wrapper">
                    <img
                        :src="`/media/images/web_skill_ranking/numbers-${String(current_position).padStart(2, '0')}.svg`"
                        alt="Position"
                        class="position-svg"
                    />
                </div>
            </template>

            <template #item-avatar="{ photo, id }">
                <div
                    class="avatar-wrapper"
                    :style="{ backgroundColor: avatarBackground(id) }"
                >
                    <img
                        v-if="photo"
                        :src="photo"
                        class="profile-avatar"
                        alt="Avatar"
                    />
                </div>
            </template>

            <template #header-name_address>
                <div class="header-multiline">
                    <span>{{ $trans("web_skill_ranking.name") }}</span>
                    <span>{{ $trans("web_skill_ranking.address") }}</span>
                </div>
            </template>

            <template #item-name_address="{ profile_name, address }">
                <div class="cell-multiline">
                    <div class="font-bold">{{ profile_name || "-" }}</div>
                    <div class="font-bold">{{ address || "-" }}</div>
                </div>
            </template>

            <template #header-region_availability>
                <div class="header-multiline">
                    <span>{{ $trans("web_skill_ranking.region") }}</span>
                    <span>{{ $trans("web_skill_ranking.availability") }}</span>
                </div>
            </template>

            <template #item-region_availability="item">
                <div class="cell-multiline">
                    <div class="font-bold flex items-center" style="display: flex; align-items: center;">
                        {{ item.region || item.country_name || "-" }}
                        <EditButton
                            class="ml-2 region-edit-btn"
                            :onClick="() => handleRegionEditClick(item)"
                        />
                    </div>
                    <div class="availability-status-wrapper mt-1">
                        <div
                            class="availability-status"
                            :class="availabilityClass(item.availability)"
                        ></div>
                        <span class="font-bold">{{
                            $trans(
                                "web_skill_ranking.availability_status_types." +
                                    item.availability,
                            )
                        }}</span>
                    </div>
                </div>
            </template>

            <template #header-score_status>
                <div class="header-multiline">
                    <span>{{ $trans("web_skill_ranking.score") }}</span>
                    <span>{{ $trans("web_skill_ranking.status") }}</span>
                </div>
            </template>

            <template
                #item-score_status="{ score, current_position, last_position }"
            >
                <div class="cell-multiline">
                    <div class="font-bold">
                        {{
                            maxScore > 0 && score
                                ? Math.round((score / maxScore) * 100)
                                : 0
                        }}/100
                    </div>
                    <div class="font-bold">
                        <span
                            :class="
                                positionStatusClass(
                                    current_position,
                                    last_position,
                                )
                            "
                        >
                            {{
                                positionStatusText(
                                    current_position,
                                    last_position,
                                )
                            }}
                        </span>
                    </div>
                </div>
            </template>

            <template #header-ai_status>
                <div class="header-multiline">
                    <span>{{
                        $trans("web_skill_ranking.ai_status") || "AI Status"
                    }}</span>
                </div>
            </template>

            <template #item-ai_status="{ ai_content_status }">
                <div class="cell-multiline">
                    <div class="font-bold">
                        <span
                            v-if="ai_content_status === 'in_progress'"
                            class="flex items-center font-bold"
                        >
                            <el-icon class="is-loading mr-1"
                                ><Loading
                            /></el-icon>
                            {{
                                $trans("web_skill_ranking.in_progress") ||
                                "In progress..."
                            }}
                        </span>
                        <span
                            v-else-if="ai_content_status === 'success'"
                            class="text-green font-bold"
                        >
                            {{
                                $trans("web_skill_ranking.success") || "Success"
                            }}
                        </span>
                        <span v-else>{{ ai_content_status || "-" }}</span>
                    </div>
                </div>
            </template>

            <template #header-actions>
                <div class="actions-header-wrapper">
                    <span>{{ $trans("web_skill_ranking.photo") || "Photo" }}</span>
                    <span>{{ $trans("web_skill_ranking.ranking") || "Ranking" }}</span>
                    <span>{{ $trans("web_skill_ranking.edit") || "Edit" }}</span>
                </div>
            </template>

            <template #item-actions="item">
                <div class="action-wrapper">
                    <div class="action-group">
                        <el-switch
                            v-model="item.use_default_background_photo"
                            @change="
                                (val) => handleBackgroundSwitchChange(item.id, val)
                            "
                            inline-prompt
                            :active-text="$trans('messages.yes') || 'Y'"
                            :inactive-text="$trans('messages.no') || 'N'"
                            style="
                                --el-switch-on-color: #41abc4;
                                --el-switch-off-color: #999;
                            "
                        />
                        <el-button
                            type="primary"
                            plain
                            class="action-btn"
                            @click="handleUploadClick(item.id)"
                        >
                            <el-icon><Upload /></el-icon>
                        </el-button>
                    </div>

                    <div class="action-group">
                        <el-switch
                            v-model="item.is_excluded"
                            @change="(val) => handleExclusionSwitchChange(item.id, val)"
                            inline-prompt
                            :active-value="false"
                            :inactive-value="true"
                            :active-text="$trans('messages.yes') || 'Y'"
                            :inactive-text="$trans('messages.no') || 'N'"
                            style="
                                --el-switch-on-color: #41abc4;
                                --el-switch-off-color: #999;
                            "
                        />
                    </div>

                    <div class="action-group">
                        <div class="edit-action-container">
                            <EditButton
                                :onClick="
                                    item.ai_content_status === 'in_progress'
                                        ? () => {}
                                        : () => handleEditClick(item.id)
                                "
                                :class="{
                                    'disabled-action':
                                        item.ai_content_status === 'in_progress',
                                }"
                            />
                        </div>
                        <el-checkbox
                            v-model="selectedProfiles"
                            :label="item.id"
                            class="round-checkbox"
                        />
                    </div>
                </div>
            </template>
        </EasyDataTable>

        <div v-if="WebSkillRankingStore.excludedProfiles.length > 0" class="excluded-section">
            <h3 class="excluded-title">{{ $trans('web_skill_ranking.excluded_profiles') || 'Excluded Profiles' }}</h3>
            
            <EasyDataTable
                :border-cell="false"
                :buttons-pagination="false"
                :headers="excludedHeaders"
                :items="WebSkillRankingStore.excludedProfiles"
                :rows-per-page="10"
                hide-footer
                table-class-name="custom-ranking-table excluded-table"
            >
                <template #item-avatar="{ photo, id }">
                    <div
                        class="avatar-wrapper"
                        :style="{ backgroundColor: avatarBackground(id) }"
                    >
                        <img
                            v-if="photo"
                            :src="photo"
                            class="profile-avatar"
                            alt="Avatar"
                        />
                    </div>
                </template>

                <template #header-name_address>
                    <div class="header-multiline">
                        <span>{{ $trans("web_skill_ranking.name") }}</span>
                        <span>{{ $trans("web_skill_ranking.address") }}</span>
                    </div>
                </template>

                <template #item-name_address="{ profile_name, address }">
                    <div class="cell-multiline">
                        <div class="font-bold">{{ profile_name || "-" }}</div>
                        <div class="font-bold">{{ address || "-" }}</div>
                    </div>
                </template>

                <template #header-region_availability>
                    <div class="header-multiline">
                        <span>{{ $trans("web_skill_ranking.region") }}</span>
                        <span>{{ $trans("web_skill_ranking.availability") }}</span>
                    </div>
                </template>

                <template #item-region_availability="item">
                    <div class="cell-multiline">
                        <div class="font-bold flex items-center" style="display: flex; align-items: center;">
                            {{ item.region || item.country_name || "-" }}
                            <EditButton
                                class="ml-2 region-edit-btn"
                                :onClick="() => handleRegionEditClick(item)"
                            />
                        </div>
                        <div class="availability-status-wrapper mt-1">
                            <div
                                class="availability-status"
                                :class="availabilityClass(item.availability)"
                            ></div>
                            <span class="font-bold">{{
                                $trans(
                                    "web_skill_ranking.availability_status_types." +
                                        item.availability,
                                )
                            }}</span>
                        </div>
                    </div>
                </template>

                <template #header-score_status>
                    <div class="header-multiline">
                        <span>{{ $trans("web_skill_ranking.score") }}</span>
                    </div>
                </template>

                <template #item-score_status="{ score }">
                    <div class="cell-multiline">
                        <div class="font-bold">
                            {{
                                maxScore > 0 && score
                                    ? Math.round((score / maxScore) * 100)
                                    : 0
                            }}/100
                        </div>
                    </div>
                </template>

                <template #header-ai_status>
                    <div class="header-multiline">
                        <span>{{
                            $trans("web_skill_ranking.ai_status") || "AI Status"
                        }}</span>
                    </div>
                </template>

                <template #item-ai_status="{ ai_content_status }">
                    <div class="cell-multiline">
                        <div class="font-bold">
                            <span
                                v-if="ai_content_status === 'in_progress'"
                                class="flex items-center font-bold"
                            >
                                <el-icon class="is-loading mr-1"
                                    ><Loading
                                /></el-icon>
                                {{
                                    $trans("web_skill_ranking.in_progress") ||
                                    "In progress..."
                                }}
                            </span>
                            <span
                                v-else-if="ai_content_status === 'success'"
                                class="text-green font-bold"
                            >
                                {{
                                    $trans("web_skill_ranking.success") || "Success"
                                }}
                            </span>
                            <span v-else>{{ ai_content_status || "-" }}</span>
                        </div>
                    </div>
                </template>

                <template #header-actions>
                    <div class="actions-header-wrapper">
                        <span>{{ $trans("web_skill_ranking.photo") || "Photo" }}</span>
                        <span>{{ $trans("web_skill_ranking.ranking") || "Ranking" }}</span>
                        <span>{{ $trans("web_skill_ranking.edit") || "Edit" }}</span>
                    </div>
                </template>

                <template #item-actions="item">
                    <div class="action-wrapper">
                        <div class="action-group">
                            <el-switch
                                v-model="item.use_default_background_photo"
                                @change="
                                    (val) => handleBackgroundSwitchChange(item.id, val)
                                "
                                inline-prompt
                                :active-text="$trans('messages.yes') || 'Y'"
                                :inactive-text="$trans('messages.no') || 'N'"
                                style="
                                    --el-switch-on-color: #41abc4;
                                    --el-switch-off-color: #999;
                                "
                            />
                            <el-button
                                type="primary"
                                plain
                                class="action-btn"
                                @click="handleUploadClick(item.id)"
                            >
                                <el-icon><Upload /></el-icon>
                            </el-button>
                        </div>

                        <div class="action-group">
                            <el-switch
                                v-model="item.is_excluded"
                                @change="(val) => handleExclusionSwitchChange(item.id, val)"
                                inline-prompt
                                :active-value="false"
                                :inactive-value="true"
                                :active-text="$trans('messages.yes') || 'Y'"
                                :inactive-text="$trans('messages.no') || 'N'"
                                style="
                                    --el-switch-on-color: #41abc4;
                                    --el-switch-off-color: #999;
                                "
                            />
                        </div>

                        <div class="action-group">
                            <div class="edit-action-container">
                                <EditButton
                                    :onClick="
                                        item.ai_content_status === 'in_progress'
                                            ? () => {}
                                            : () => handleEditClick(item.id)
                                    "
                                    :class="{
                                        'disabled-action':
                                            item.ai_content_status === 'in_progress',
                                    }"
                                />
                            </div>
                            <el-switch
                                v-if="false"
                            />
                            <el-checkbox
                                v-model="selectedProfiles"
                                :label="item.id"
                                class="round-checkbox"
                            />
                        </div>
                    </div>
                </template>
            </EasyDataTable>
        </div>

        <UploadPhotoSkillRankingModal
            v-if="showUploadModal"
            v-model="showUploadModal"
            :profile="activeProfile"
            @submit="handlePhotoUploadSubmit"
        />
        <EditProfileSkillRankingModal
            v-if="showEditModal"
            v-model="showEditModal"
            :profile="activeProfile"
            @submit="handleEditSubmit"
        />

        <EditRegionSkillRankingModal />
        
    </section>
</template>

<script>
import { useWebSkillRankingStore } from "@/stores/WebSkillRankingStore";
import { mapStores } from "pinia";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import { Edit, Upload, Loading } from "@element-plus/icons-vue";
import EditButton from "./../../WebProfile/Components/EditButton.vue";
import UploadPhotoSkillRankingModal from "./../Modals/UploadPhotoSkillRankingModal.vue";
import EditProfileSkillRankingModal from "./../Modals/EditProfileSkillRankingModal.vue";
import EditRegionSkillRankingModal from "./../Modals/EditRegionSkillRankingModal.vue";

export default {
    name: "WebSkillRankingTable",
    components: {
        EasyDataTable,
        Edit,
        Upload,
        Loading,
        EditButton,
        UploadPhotoSkillRankingModal,
        EditProfileSkillRankingModal,
        EditRegionSkillRankingModal,
    },
    data() {
        return {
            selectedProfiles: [],
            showUploadModal: false,
            showEditModal: false,
            isRegionSaving: false,
            pollInterval: null,
            headers: [
                { text: "", value: "position", width: 80 },
                { text: "", value: "avatar", width: 90, align: "center" },
                { text: "", value: "name_address" },
                { text: "", value: "region_availability" },
                { text: "", value: "score_status" },
                { text: "", value: "ai_status" },
                { text: "", value: "actions", width: 260 },
            ],
            excludedHeaders: [
                { text: "", value: "avatar", width: 90, align: "center" },
                { text: "", value: "name_address" },
                { text: "", value: "region_availability" },
                { text: "", value: "score_status" },
                { text: "", value: "ai_status" },
                { text: "", value: "actions", width: 260 },
            ],
        };
    },
    computed: {
        ...mapStores(useWebSkillRankingStore),
        isGenerateActive() {
            return this.selectedProfiles.length > 0;
        },
        isAnyGenerating() {
            return this.WebSkillRankingStore.profiles.some(
                (p) => p.ai_content_status === "in_progress",
            );
        },
        maxScore() {
            if (
                !this.WebSkillRankingStore.profiles ||
                this.WebSkillRankingStore.profiles.length === 0
            )
                return 0;
            return Math.max(
                ...this.WebSkillRankingStore.profiles.map((p) => p.score || 0),
            );
        },
    },
    mounted() {
        this.startPolling();
    },
    beforeUnmount() {
        this.stopPolling();
    },
    methods: {
        startPolling() {
            if (this.pollInterval) return;
            this.pollInterval = setInterval(async () => {
                if (this.isAnyGenerating) {
                    await this.WebSkillRankingStore.refreshProfiles();
                }
            }, 3000);
        },
        stopPolling() {
            if (this.pollInterval) clearInterval(this.pollInterval);
        },
        availabilityClass(availability) {
            return {
                "border-availability-available": availability === "available",
                "border-availability-on-request": availability === "on_request",
                "border-availability-unavailable":
                    availability === "unavailable",
            };
        },
        avatarBackground(profileId) {
            const colors = ["#41abc4", "#aa916a", "#ff6b65"];
            return colors[Math.abs(profileId) % colors.length];
        },
        positionStatusText(current, last) {
            if (last === null || last === undefined) {
                return this.$trans("web_skill_ranking.new_position") || "New";
            }
            if (last === current) {
                return (
                    this.$trans("web_skill_ranking.published") || "Published"
                );
            }
            const diff = last - current;
            return diff > 0 ? `+${diff}` : `${diff}`;
        },
        positionStatusClass(current, last) {
            if (last === null || last === undefined) return "status-new";
            if (last === current) return "status-published";
            const diff = last - current;
            return diff > 0 ? "status-up" : "status-down";
        },
        async handleBackgroundSwitchChange(profileId, useDefault) {
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.setDefaultBackgroundPhoto(
                    profileId,
                    useDefault,
                );
                this.$message.success(
                    this.$trans("web_skill_ranking.data_saved") ||
                        "Data saved successfully.",
                );
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
        async handleExclusionSwitchChange(profileId, isExcluded) {
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.toggleProfileExclusion(
                    profileId,
                    isExcluded,
                );
                this.$message.success(
                    this.$trans("web_skill_ranking.data_saved") ||
                        "Data saved successfully.",
                );
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
        handleUploadClick(profileId) {
            this.activeProfile = this.WebSkillRankingStore.profiles.find(
                (p) => p.id === profileId,
            );
            if (this.activeProfile) this.showUploadModal = true;
        },
        async handlePhotoUploadSubmit(file) {
            if (!this.activeProfile) return;
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.uploadProfilePhoto(
                    this.activeProfile.id,
                    file,
                );
                this.showUploadModal = false;
                this.$message.success(
                    this.$trans(
                        "web_skill_ranking.admin.photo_uploaded_successfully",
                    ) || "Photo uploaded successfully.",
                );
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
        handleEditClick(profileId) {
            this.activeProfile = this.WebSkillRankingStore.profiles.find(
                (p) => p.id === profileId,
            );
            if (this.activeProfile) this.showEditModal = true;
        },
        async handleEditSubmit(payload) {
            if (!this.activeProfile) return;
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.updateProfileTexts(
                    this.activeProfile.id,
                    payload.headline,
                    payload.summary,
                );
                this.showEditModal = false;
                this.$message.success(
                    this.$trans("web_skill_ranking.data_saved") ||
                        "Data saved successfully.",
                );
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
        handleRegionEditClick(item) {
            this.WebSkillRankingStore.openEditRegionModal(item);
        },
        async handleRegionSubmit() {
            if (!this.activeProfile) return;
            this.isRegionSaving = true;
            try {
                await this.WebSkillRankingStore.updateProfileCustomRegion(
                    this.activeProfile.id,
                    this.activeRegionValue,
                );
                this.showRegionModal = false;
                this.$message.success(
                    this.$trans("web_skill_ranking.data_saved") ||
                        "Data saved successfully.",
                );
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.isRegionSaving = false;
            }
        },
        async handleGenerateText() {
            if (!this.isGenerateActive) return;
            try {
                this.$functions?.showLoader();
                await this.WebSkillRankingStore.generateAiContentForProfiles(
                    this.selectedProfiles,
                );
                this.$message.success(
                    this.$trans("web_skill_ranking.generation_started") ||
                        "Generation started.",
                );
                this.selectedProfiles = [];
                this.startPolling();
            } catch (error) {
                console.error(error);
                this.$message.error(
                    this.$trans("web_skill_ranking.something_went_wrong") ||
                        "Something went wrong.",
                );
            } finally {
                this.$functions?.hideLoader();
            }
        },
    },
};
</script>

<style scoped>
.ranking-table-section {
    padding: 1rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
    position: relative;
}
.table-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
}
.generate-text-btn {
    background-color: #41abc4;
    border-color: #41abc4;
    cursor: pointer;
    font-size: 16px !important;
    font-weight: 700 !important;
}
.generate-text-btn:hover {
    background-color: #3693a9;
    border-color: #3693a9;
}
.generate-text-btn.is-disabled {
    background-color: #999 !important;
    border-color: #999 !important;
    cursor: not-allowed !important;
}
.cell-multiline,
.header-multiline {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.cell-multiline {
    padding: 8px 0;
}
.font-bold {
    font-weight: 700;
}
.text-sm {
    font-size: 0.9em;
    color: #555;
}
.mt-1 {
    margin-top: 4px;
}
.position-wrapper {
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #41abc4;
    width: 65px;
    border-radius: 5px;
}
.position-svg {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    filter: brightness(0) invert(1);
}
.avatar-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    overflow: hidden;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
}
.profile-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.availability-status-wrapper {
    display: flex;
    align-items: center;
}
.availability-status {
    width: 0;
    height: 0;
    border-radius: 50%;
    border-width: 6px;
    border-style: solid;
    margin-right: 6px;
}
.border-availability-available {
    border-color: #41abc4;
}
.border-availability-on-request {
    border-color: #ef9630;
}
.border-availability-unavailable {
    border-color: #ff6b65;
}
.actions-header-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 100%;
    text-align: center;
    gap: 6px;
}
.actions-header-wrapper span {
    font-size: 14px;
    font-weight: 700;
    color: #555;
}
.action-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    padding: 0 2px;
}
.action-group {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background-color: rgba(255, 255, 255, 0.4);
    padding: 4px;
    border-radius: 6px;
    height: 100%;
    min-height: 44px;
}
.action-btn {
    width: 37px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-color: #ccc;
    color: #555;
    background-color: #41abc4;
    color: white;
    border-color: #41abc4;
}
.action-btn :deep(.el-icon) {
    font-size: 30px !important;
}
.action-btn:hover {
    border-color: #3693a9;
    background-color: #3693a9;
    color: white;
}
.edit-action-container {
    width: 37px;
    display: flex;
    justify-content: center;
}
.edit-button-vue[data-v-088a6478] {
    width: 24px !important;
    height: 24px !important;
}
.round-checkbox {
    margin-top: 0;
    margin-right: 0;
    display: flex;
    align-items: center;
}
.round-checkbox :deep(.el-checkbox__inner) {
    border-radius: 50%;
    width: 25px;
    height: 25px;
    border: 2px solid #ccc;
}
.round-checkbox :deep(.el-checkbox__inner::after) {
    height: 12px;
    width: 6px;
    left: 10px;
    top: 10px;
    border-width: 2px;
}
.round-checkbox :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #41abc4;
    border-color: #41abc4;
}
.round-checkbox :deep(.el-checkbox__label) {
    display: none;
}
.disabled-action {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed !important;
}
.status-new {
    color: #ff6b65;
    font-weight: bold;
}
.status-published {
    color: #555;
    font-weight: bold;
}
.status-up {
    color: #67c23a;
    font-weight: bold;
}
.status-down {
    color: #ff6b65;
    font-weight: bold;
}
.text-green {
    color: #67c23a;
}
.excluded-section {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 2px dashed #ddd;
}
.excluded-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #555;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
}
:deep(.excluded-table) {
    opacity: 0.8;
}
:deep(.region-edit-btn.edit-button-vue) {
  width: 9px !important;
  height: 9px !important;
}
:deep(.region-edit-btn.edit-button-vue:hover .edit-button-vue-line-left) {
  width: 13px !important;
}
:deep(.region-edit-btn.edit-button-vue > img) {
  width: 9px !important;
}
:deep(.custom-ranking-table) {
    --easy-table-border: none;
    --easy-table-row-border: none;
    --easy-table-header-font-size: 13px;
    --easy-table-body-row-font-size: 14px;
    --easy-table-header-background-color: transparent;
    --easy-table-body-row-background-color: transparent;
    --easy-table-body-row-font-color: #000;
    --easy-table-body-row-hover-font-color: #000;
    --easy-table-body-row-hover-background-color: transparent;
}
:deep(.vue3-easy-data-table__main table) {
    border-collapse: separate !important;
    border-spacing: 0 !important;
}
:deep(.vue3-easy-data-table__main),
:deep(.vue3-easy-data-table__body tr) {
    background-color: transparent !important;
}
:deep(.vue3-easy-data-table__main tr td) {
    background-color: #ffffff !important;
    border: 4.5px solid #ededed !important;
    font-weight: 500 !important;
    color: #000 !important;
    font-size: 15px !important;
    padding: 0.5rem !important;
    vertical-align: middle !important;
}
:deep(.vue3-easy-data-table__main tr td:first-child) {
    padding: 0 !important;
    text-align: center !important;
}
:deep(.vue3-easy-data-table__main tr td:nth-child(2)),
:deep(.excluded-table .vue3-easy-data-table__main tr td:first-child) {
    text-align: center !important;
    padding: 0 !important;
}
:deep(.vue3-easy-data-table__main tr td:last-child) {
    background-color: #d2e1e5 !important;
    padding: 0 !important;
    text-align: center !important;
}
:deep(.vue3-easy-data-table__main tr th) {
    background-color: transparent !important;
    border: none !important;
    pointer-events: none;
    padding-left: 10px;
    vertical-align: bottom;
    padding-bottom: 10px;
    font-weight: 700 !important;
    color: #000 !important;
    font-size: 16px !important;
}
:deep(.vue3-easy-data-table__body tr:hover td) {
    background-color: #ffffff !important;
}
:deep(.vue3-easy-data-table__body tr:hover td:last-child) {
    background-color: #d2e1e5 !important;
}
:deep(.vue3-easy-data-table__body) {
    background-color: #ededed !important;
}
:deep(.vue3-easy-data-table__main tr td:nth-child(2) > div),
:deep(.excluded-table .vue3-easy-data-table__main tr td:first-child > div) {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 85%;
    height: 85%;
    left: 6px;
    top: 6px;
}
</style>